import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { AlertTriangle, X } from 'lucide-react'
import { Button } from './button'
import { Input } from './input'

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'default', // 'default' or 'destructive'
  requiresTyping = false,
  confirmationText = 'DELETE',
  onConfirm,
  onCancel
}) {
  const [typedText, setTypedText] = useState('')
  const [isClosing, setIsClosing] = useState(false)

  // Reset typed text when dialog opens
  useEffect(() => {
    if (open) {
      setTypedText('')
      setIsClosing(false)
    }
  }, [open])

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && open) {
        handleCancel()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [open])

  const handleConfirm = () => {
    if (requiresTyping && typedText !== confirmationText) {
      return
    }
    
    setIsClosing(true)
    setTimeout(() => {
      onConfirm?.()
      onOpenChange?.(false)
    }, 200)
  }

  const handleCancel = () => {
    setIsClosing(true)
    setTimeout(() => {
      onCancel?.()
      onOpenChange?.(false)
    }, 200)
  }

  // Handle Enter key for confirm
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (!requiresTyping || typedText === confirmationText)) {
      handleConfirm()
    }
  }

  if (!open) return null

  const isDestructive = variant === 'destructive'
  const canConfirm = !requiresTyping || typedText === confirmationText

  // Portalled to <body>. The overlay and panel are `fixed z-50`, but z-index is
  // only comparable inside one stacking context and every `.card-glass` sets
  // `isolation: isolate` — so a dialog rendered inline inside a card was
  // trapped in that card's context and painted UNDER the cards after it in the
  // list (ticket 4d71d9f0: the Delete confirm sat behind the active discount
  // codes). At the document root there is nothing left to be trapped by.
  return createPortal(
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black/50 z-50 transition-opacity duration-200 ${
          isClosing ? 'opacity-0' : 'opacity-100'
        }`}
        onClick={handleCancel}
      />

      {/* Dialog */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div
          className={`bg-card border rounded-lg shadow-xl max-w-md w-full pointer-events-auto transition-all duration-300 ${
            isClosing ? 'scale-95 opacity-0' : 'scale-100 opacity-100'
          }`}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={handleKeyDown}
        >
          {/* Header */}
          <div className="flex items-start justify-between p-6 pb-4">
            <div className="flex items-start gap-3">
              {isDestructive && (
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center">
                  <AlertTriangle className="h-5 w-5 text-destructive" />
                </div>
              )}
              <div>
                <h2 className="text-xl font-bold text-foreground">{title}</h2>
              </div>
            </div>
            <button
              onClick={handleCancel}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 pb-6">
            <p className="text-muted-foreground mb-4">{description}</p>

            {requiresTyping && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">
                  Type <span className="font-mono font-bold">{confirmationText}</span> to confirm:
                </label>
                <Input
                  value={typedText}
                  onChange={(e) => setTypedText(e.target.value)}
                  placeholder={confirmationText}
                  className="font-mono"
                  autoFocus
                />
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 bg-muted/30 border-t">
            <Button
              variant="outline"
              onClick={handleCancel}
            >
              {cancelText}
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={!canConfirm}
            >
              {confirmText}
            </Button>
          </div>
        </div>
      </div>
    </>,
    document.body,
  )
}
